import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Calendar, Clock, Eye,
  MessageCircle, Copy, CheckCheck, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { FadeIn, Reveal } from "../components/motion/Reveal";
import BlogCard from "../components/BlogCard";
import PageMeta from "../components/PageMeta";

// Injects scoped styles for HTML rich content from the CMS
const ArticleContent = ({ html }) => (
  <div
    className="article-body"
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setPost(null);
    setError(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="blog-detail-loading">
        <Loader2 size={30} className="animate-spin text-brand-red" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-[800px] flex-col items-center justify-center px-5 py-24 text-center">
        <p className="font-display text-3xl font-semibold">Post not found</p>
        <p className="mt-3 text-neutral-500">This article may have been moved or removed.</p>
        <Link to="/blog" className="btn-dark mt-8">Back to Blog</Link>
      </div>
    );
  }

  const date = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const whatsappMsg = encodeURIComponent(
    `Check out this article from Pickleballgearz: ${post.title} — ${window.location.href}`
  );

  return (
    <article data-testid="blog-detail-page">
      <PageMeta
        title={post.meta_title || post.title}
        description={post.meta_description || post.summary}
        image={post.cover_image}
        canonical={`http://pickleballgearz.com/blog/${slug}`}
      />

      {/* Cover image hero */}
      {post.cover_image ? (
        <div className="relative h-[55vh] min-h-[400px] overflow-hidden bg-brand-ink" data-testid="blog-cover">
          <img
            src={post.cover_image}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover opacity-60"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/50 to-transparent" />
          <div className="relative mx-auto flex h-full max-w-[900px] flex-col justify-end px-5 pb-12 lg:px-0">
            {post.category && (
              <FadeIn>
                <span className="mb-4 inline-block bg-brand-red px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                  {post.category}
                </span>
              </FadeIn>
            )}
            <FadeIn delay={0.1}>
              <h1 className="font-display text-3xl font-semibold leading-tight tracking-tighter text-white sm:text-4xl md:text-5xl">
                {post.title}
              </h1>
            </FadeIn>
          </div>
        </div>
      ) : (
        <div className="border-b border-black/10 bg-white">
          <div className="mx-auto max-w-[900px] px-5 pb-10 pt-16 lg:px-0">
            {post.category && (
              <FadeIn>
                <span className="mb-4 inline-block bg-brand-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                  {post.category}
                </span>
              </FadeIn>
            )}
            <FadeIn delay={0.1}>
              <h1 className="font-display text-4xl font-semibold leading-tight tracking-tighter sm:text-5xl">
                {post.title}
              </h1>
            </FadeIn>
          </div>
        </div>
      )}

      {/* Meta bar */}
      <div className="border-b border-black/10 bg-brand-smoke">
        <div className="mx-auto flex max-w-[900px] flex-wrap items-center gap-5 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500 lg:px-0">
          {date && (
            <span className="flex items-center gap-1.5">
              <Calendar size={11} className="text-brand-red" /> {date}
            </span>
          )}
          {post.author && <span>By {post.author}</span>}
          {post.read_time && (
            <span className="flex items-center gap-1.5">
              <Clock size={11} className="text-brand-red" /> {post.read_time} min read
            </span>
          )}
          {typeof post.views_count === "number" && (
            <span className="flex items-center gap-1.5">
              <Eye size={11} className="text-brand-red" /> {post.views_count.toLocaleString()} views
            </span>
          )}

          {/* Share buttons */}
          <div className="ml-auto flex gap-2">
            <a
              href={`https://wa.me/?text=${whatsappMsg}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on WhatsApp"
              className="flex h-8 w-8 items-center justify-center border border-black/10 text-neutral-500 hover:border-green-500 hover:text-green-600 transition-colors"
            >
              <MessageCircle size={13} />
            </a>
            <button
              onClick={copyLink}
              aria-label="Copy link"
              className="flex h-8 w-8 items-center justify-center border border-black/10 text-neutral-500 hover:border-brand-ink hover:text-brand-ink transition-colors"
            >
              {copied ? <CheckCheck size={13} className="text-green-600" /> : <Copy size={13} />}
            </button>
          </div>
        </div>
      </div>

      {/* Article body */}
      <div className="mx-auto max-w-[900px] px-5 py-14 lg:px-0">
        {post.summary && (
          <Reveal>
            <p className="mb-8 border-l-2 border-brand-red pl-5 text-lg leading-relaxed text-neutral-600 italic">
              {post.summary}
            </p>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <ArticleContent html={post.content || "<p>No content available.</p>"} />
        </Reveal>

        {/* Back link */}
        <Reveal delay={0.15} className="mt-16 border-t border-black/10 pt-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link to="/blog" className="btn-outline" data-testid="blog-detail-back">
              <ArrowLeft size={16} /> Back to Blog
            </Link>
            <Link to="/inquiry" className="btn-primary" data-testid="blog-detail-inquiry">
              Request a Quote <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Related Posts */}
      {post.related && post.related.length > 0 && (
        <section className="border-t border-black/10 bg-brand-smoke py-16" data-testid="blog-related">
          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
            <Reveal>
              <p className="mono-caption mb-6 text-neutral-500">Continue Reading</p>
              <h2 className="font-display text-2xl font-semibold tracking-tight">
                Related Articles
              </h2>
            </Reveal>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {post.related.map((related, i) => (
                <BlogCard key={related.id} post={related} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogDetail;
